import { ColDef, GridApi } from '@ag-grid-community/core';
import { computed, signal, WritableSignal } from '@angular/core';

import { getEncodedAttributeName } from '@shared/utils/get-encoded-attribute-name';

import { BalanceSheetAttributesContext } from './balance-sheet-attributes.context';
import { updateDuplicateHeaderTooltips } from './new-manage-balance-sheets-accounts.utils';
import { getAttributeColumn } from '../components/balance-sheet-account-grid/grid-config/grid-config';

export interface BalanceSheetHeaderParams {
  attributesContext: BalanceSheetAttributesContext;
  onAttributeDeleteEffect?: (attributeName: string, state: HeaderState) => void;
  onAttributeRenameEffect?: (attributeName: string, state: HeaderState) => void;
}

export type HeaderState = Record<string, HeaderStateEntry>;

export type HeaderStateEntry = {
  added: boolean;
  deleted: boolean;
  renamed: boolean;
  renamedValue: string;
};

export function useBalanceSheetHeaderContext(params: BalanceSheetHeaderParams) {
  const headerState = signal<HeaderState>({});
  const hasDuplicateHeaders = signal(false);

  let newColumnCounter = 0;

  const hasUnsavedChanges = computed(() => {
    const state = headerState();

    return Object.values(state).some((s) => {
      if (s.added && s.deleted) return false;

      return s.added || s.renamed || s.deleted;
    });
  });

  const headerErrorTooltip = computed(() => {
    // Check for blank attribute names
    const state = headerState();
    const hasBlank = Object.entries(state).some(([colId, s]) => {
      // If renamed, check renamedValue; else, colId
      const effectiveName = s.renamed ? s.renamedValue : colId;

      // Only care about columns that are not deleted
      return !s.deleted && effectiveName.trim() === '';
    });

    if (hasDuplicateHeaders() || hasBlank) {
      return 'Please resolve errors to continue';
    }

    return '';
  });

  // Actions
  const onAttributeDelete = (attributeName: string, gridApi: GridApi) => {
    handleAttributeDelete(attributeName, gridApi, headerState, hasDuplicateHeaders);

    if (params.onAttributeDeleteEffect) {
      params.onAttributeDeleteEffect(attributeName, headerState());
    }
  };

  const onAttributeRename = (attributeName: string, newName: string, gridApi: GridApi) => {
    handleAttributeRename(attributeName, newName, gridApi, headerState, hasDuplicateHeaders);

    if (params.onAttributeRenameEffect) {
      params.onAttributeRenameEffect(attributeName, headerState());
    }
  };

  const onAddNewAttribute = (gridApi: GridApi) => {
    newColumnCounter++;
    handleAddNewAttribute({
      attributesContext: params.attributesContext,
      gridApi,
      hasDuplicateHeaders,
      headerState,
      newColumnCounter,
      onAttributeDelete,
      onAttributeRename,
    });
  };

  const onClearState = () => {
    headerState.set({});
  };

  return {
    hasDuplicateHeaders,
    hasUnsavedChanges,
    headerErrorTooltip,
    headerState,
    onAddNewAttribute,
    onAttributeDelete,
    onAttributeRename,
    onClearState,
  };
}

function addNewAttributeColumn({
  attributesContext,
  gridApi,
  hasDuplicateHeaders,
  headerState,
  newAttributeName,
  onAttributeDelete,
  onAttributeRename,
}: {
  attributesContext: BalanceSheetAttributesContext;
  gridApi: GridApi;
  hasDuplicateHeaders: WritableSignal<boolean>;
  headerState: WritableSignal<HeaderState>;
  newAttributeName: string;
  onAttributeDelete: (attributeName: string, gridApi: GridApi) => void;
  onAttributeRename: (attributeName: string, newName: string, gridApi: GridApi) => void;
}) {
  const { getAttributeOptions, onAddAttributeOption } = attributesContext;
  const columnDefs = gridApi.getColumnDefs()!;
  const programColumnIndex = columnDefs.findIndex(
    (col: ColDef) => (col as ColDef).field === 'program'
  );

  const newColumnDef = getAttributeColumn({
    colId: newAttributeName,
    field: `attributes.${getEncodedAttributeName(newAttributeName)}`,
    headerName: '',
    headerState,
    isNewColumn: true,
    items: () => getAttributeOptions(newAttributeName),
    onAddOption: (option) => onAddAttributeOption(newAttributeName, option),
    onDelete: () => onAttributeDelete(newAttributeName, gridApi),
    onRename: (newName: string) => onAttributeRename(newAttributeName, newName, gridApi),
  });

  const newColumnDefs = [
    ...columnDefs.slice(0, programColumnIndex),
    newColumnDef,
    ...columnDefs.slice(programColumnIndex),
  ];

  const { columnDefs: updatedColumnDefs, hasDuplicate } = updateDuplicateHeaderTooltips(
    newColumnDefs,
    headerState()
  );

  hasDuplicateHeaders.set(hasDuplicate);
  gridApi.setGridOption('columnDefs', updatedColumnDefs);

  // Wait for the column to be added and rendered
  requestAnimationFrame(() => {
    // Ensure the column is visible
    gridApi.ensureColumnVisible(newAttributeName);
  });
}

function deleteAttributeColumn(
  gridApi: GridApi,
  attributeName: string,
  headerState: WritableSignal<HeaderState>,
  hasDuplicateHeaders: WritableSignal<boolean>
) {
  const currentColumns = gridApi.getColumnDefs() as ColDef[];
  const newColumns = currentColumns.filter(
    (col: ColDef) => (col as ColDef).colId !== attributeName
  );
  const { columnDefs: updatedColumnDefs, hasDuplicate } = updateDuplicateHeaderTooltips(
    newColumns,
    headerState()
  );

  hasDuplicateHeaders.set(hasDuplicate);
  gridApi.setGridOption('columnDefs', updatedColumnDefs);
}

function handleAddNewAttribute({
  attributesContext,
  gridApi,
  hasDuplicateHeaders,
  headerState,
  newColumnCounter,
  onAttributeDelete,
  onAttributeRename,
}: {
  attributesContext: BalanceSheetAttributesContext;
  gridApi: GridApi;
  hasDuplicateHeaders: WritableSignal<boolean>;
  headerState: WritableSignal<HeaderState>;
  newColumnCounter: number;
  onAttributeDelete: (attributeName: string, gridApi: GridApi) => void;
  onAttributeRename: (attributeName: string, newName: string, gridApi: GridApi) => void;
}) {
  const newAttributeName = `NAME${newColumnCounter}`;

  headerState.set({
    ...headerState(),
    [newAttributeName]: {
      added: true,
      deleted: false,
      renamed: false,
      renamedValue: '',
    },
  });

  if (gridApi)
    addNewAttributeColumn({
      attributesContext,
      gridApi,
      hasDuplicateHeaders,
      headerState,
      newAttributeName,
      onAttributeDelete,
      onAttributeRename,
    });
}

function handleAttributeDelete(
  attributeName: string,
  gridApi: GridApi,
  headerState: WritableSignal<HeaderState>,
  hasDuplicateHeaders: WritableSignal<boolean>
) {
  headerState.set({
    ...headerState(),
    [attributeName]: {
      ...headerState()[attributeName],
      deleted: true,
    },
  });

  if (gridApi) deleteAttributeColumn(gridApi, attributeName, headerState, hasDuplicateHeaders);
}

function handleAttributeRename(
  attributeName: string,
  newName: string,
  gridApi: GridApi,
  headerState: WritableSignal<HeaderState>,
  hasDuplicateHeaders: WritableSignal<boolean>
) {
  const isSameValue = attributeName === newName;

  headerState.set({
    ...headerState(),
    [attributeName]: {
      ...headerState()[attributeName],
      renamed: !isSameValue,
      renamedValue: isSameValue ? '' : newName,
    },
  });

  if (gridApi) renameAttributeColumn(gridApi, headerState, hasDuplicateHeaders);
}

function renameAttributeColumn(
  gridApi: GridApi,
  headerState: WritableSignal<HeaderState>,
  hasDuplicateHeaders: WritableSignal<boolean>
) {
  const columnDefs = gridApi.getColumnDefs() as ColDef[];
  const { columnDefs: updatedColumnDefs, hasDuplicate } = updateDuplicateHeaderTooltips(
    columnDefs,
    headerState()
  );

  hasDuplicateHeaders.set(hasDuplicate);
  gridApi.setGridOption('columnDefs', updatedColumnDefs);
}
